import { api, LightningElement, wire } from "lwc";

import { ContentSummaryConfig, ContentSummary } from "tds/contentSummary";

import { assignedSlotNames, assignedElements } from "common/slot";
import { BooleanAttr, attrToBool } from "common/helpers";

import {
  ResizeObserverAdapter,
  ResizeObserverEntry,
} from "common/resizeObserverAdapter";
import { BREAKPOINT_WIDTHS } from "common/mediaQueryAdapter";

import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import * as locales from "./locales";

export default class extends LightningElement {
  @api summary: ContentSummary | null = null;
  @api titleMode: "anchor" | "button" | "text" = "anchor";
  private _expanded = false;
  private childCount = 0;

  private isNarrow = false;

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = null!;

  private slots: string[] = [];

  private get labelOptions() {
    return {
      showUnits: {
        count: this.childCount,
      },
      hideUnits: {
        count: this.childCount,
      },
    };
  }

  private get toggleLabel() {
    return this.expanded ? this.labels.hideUnits : this.labels.showUnits;
  }

  private get config(): ContentSummaryConfig {
    return {
      titleMode: this.titleMode,
      variant: "brick-tile",
    };
  }

  private get borderTopStyles() {
    return `border-top-color: ${this.summary!.color};`;
  }

  private onNavigate() {
    this.dispatchEvent(
      new CustomEvent("navigate", {
        detail: this.summary!,
      })
    );
  }

  private handleClick() {
    this.template
      .querySelector(".content__list")!
      .classList.add("expand__animation");
    this.dispatchEvent(new CustomEvent("expand"));
    this._expanded = !this._expanded;
  }

  private get computedMarginTop() {
    const contentList = this.template.querySelector(".content__list")!;

    if (contentList?.scrollHeight) {
      if (this.isNarrow) {
        return this.expanded ? 0 : -contentList.scrollHeight - 50;
      } else {
        return this.expanded ? 0 : -contentList.scrollHeight - 25;
      }
    }
    return 0;
  }

  private get computedStyle() {
    return `margin-top: ${this.computedMarginTop}px;`;
  }

  @api get expanded() {
    return this._expanded;
  }

  set expanded(value: BooleanAttr) {
    this._expanded = attrToBool(value);
  }

  @api ariaHidden = this.expanded ? "false" : "true";

  @api tabIndex = this.expanded ? 0 : -1;

  private get showButton() {
    return this.slots.includes("child-item");
  }

  private handleSlotChange() {
    this.slots = assignedSlotNames(this.template);

    const childSlot = this.template.querySelector(
      'slot[name="child-item"]'
    ) as HTMLSlotElement;
    this.childCount = assignedElements(childSlot).length;
  }

  private get iconSymbol() {
    return this.expanded ? "chevronup" : "chevrondown";
  }

  @wire(ResizeObserverAdapter, {
    template: "$template",
  })
  private onResize(entry: ResizeObserverEntry) {
    const { width } = entry.contentRect;
    this.isNarrow = width < BREAKPOINT_WIDTHS.md;
  }
}
